import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PawPrint } from "lucide-react";
import { api } from "@/lib/api";

const statusStyle = {
  active: "bg-[#E07A5F] text-white",
  upcoming: "bg-[#F2CC8F] text-[#2B2D42]",
  completed: "bg-[#E5DCC5] text-[#4A4E69]",
};

export const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/campaigns")
      .then((res) => setCampaigns(res.data))
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="campaigns"
      className="relative z-10 bg-[#F4EBD9] py-24 lg:py-32"
      data-testid="campaigns-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <p className="text-xs tracking-[0.3em] uppercase font-medium text-[#E07A5F] mb-6">
          Campaigns
        </p>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2B2D42] leading-tight tracking-tight max-w-2xl">
          Current &amp; upcoming donation drives.
        </h2>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && (
            <div className="text-[#4A4E69]" data-testid="campaigns-loading">
              Loading campaigns…
            </div>
          )}
          {!loading &&
            campaigns.map((c, i) => (
              <motion.article
                key={c._id || i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group overflow-hidden rounded-3xl border border-[#E5DCC5] bg-white transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(224,122,95,0.15)]"
                data-testid="campaign-card"
              >
                {c.image_url && (
                  <div className="h-52 overflow-hidden">
                    <img
                      src={c.image_url}
                      alt={c.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-7">
                  <span
                    className={`inline-block rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                      statusStyle[c.status] || statusStyle.upcoming
                    }`}
                  >
                    {c.status}
                  </span>
                  <h3 className="mt-4 font-display text-2xl font-bold text-[#2B2D42]">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-[#4A4E69] leading-relaxed">
                    {c.description}
                  </p>
                  {c.goal && (
                    <p className="mt-4 flex items-center gap-2 text-sm font-medium text-[#E07A5F]">
                      <PawPrint className="h-4 w-4" /> {c.goal}
                    </p>
                  )}
                </div>
              </motion.article>
            ))}

          {!loading && (
            <div
              className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-[#E5DCC5] p-8 text-center"
              data-testid="campaign-placeholder"
            >
              <PawPrint className="h-8 w-8 text-[#E07A5F] mb-3" />
              <p className="font-display text-xl font-bold text-[#2B2D42]">
                More coming soon
              </p>
              <p className="mt-2 text-sm text-[#4A4E69]">
                New drives are added here as our community grows.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
